import React, { useState, useEffect, useMemo } from 'react';
import { styled as styled2 } from '@compiled/react';
import debounce from 'lodash/debounce';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import { Text } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';
import { Box, xcss } from '@atlaskit/primitives';

import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

type InputProps = {
	value: number;
	onChange: (value: number) => void;
};

export const ReferenceDepthInput = ({ value, onChange }: InputProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const referenceDepthOptions = Array.from(Array(5), (_, i) => i + 1);

	return (
		<DropdownMenu
			zIndex={1000}
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					ref={triggerRef}
					iconAfter={<ChevronDown label="down icon" size="small" />}
					testId="assets-object-graph.ui.graph-config-panel.reference-depth-inputs.trigger-button"
				>
					{value}
				</Button>
			)}
			appearance="tall"
		>
			<DropdownItemGroup>
				{referenceDepthOptions.map((depth) => (
					<Box paddingInline="space.100" key={depth}>
						<DropdownItem
							onClick={() => onChange(depth)}
							testId="assets-object-graph.ui.graph-config-panel.reference-depth-inputs.depth-option"
						>
							{/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ 5: MessageDescriptorV2; 4: MessageDescriptorV2; 3: MessageDescriptorV2; 2: MessageDescriptorV2; 1: MessageDescriptorV2; helpText: MessageDescriptorV2; inboundReferencesLabel: MessageDescriptorV2; referenceDepthLabel: MessageDescriptorV2; }'. */}
							{formatMessage(messages[depth])}
						</DropdownItem>
					</Box>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export const InboundReferencesInput = ({ value, onChange }: InputProps): React.JSX.Element => {
	const [internalValue, setInternalValue] = useState(value);
	const debouncedOnChange = useMemo(() => debounce(onChange, 200), [onChange]);
	const { formatMessage } = useIntl();

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setInternalValue(value);
	}, [value]);

	return (
		<TextField
			testId="assets-object-graph.ui.graph-config-panel.reference-depth-inputs.max-of-same-type"
			type="number"
			autoComplete="off"
			min={1}
			max={50}
			value={internalValue}
			onChange={(event) => {
				// @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget'.
				const { value: updated } = event.target;
				const newValue = Number(updated);
				setInternalValue(newValue);
				debouncedOnChange(newValue);
			}}
			aria-label={
				fg('assets_enghealth_aurum-1') ? formatMessage(messages.inboundReferencesLabel) : undefined
			}
		/>
	);
};

type Props = {
	maxOfSameType: number;
	referenceDepth: number;
	onUpdateReferenceDepth: (value: number) => void;
	onUpdateMaxOfSameType: (value: number) => void;
};

export const ReferenceDepthConfigurationInputs = (props: Props): React.JSX.Element => {
	const { maxOfSameType, referenceDepth, onUpdateReferenceDepth, onUpdateMaxOfSameType } = props;
	const { formatMessage } = useIntl();
	return (
		<>
			<Box xcss={inputContainerStyles}>
				<Box xcss={labelTextStyles} as="p">
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{formatMessage(messages.referenceDepthLabel)}:
				</Box>
				<ReferenceDepthInput value={referenceDepth} onChange={onUpdateReferenceDepth} />
			</Box>

			<Box xcss={inputContainerStyles}>
				<Box xcss={labelTextStyles} as="p">
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{formatMessage(messages.inboundReferencesLabel)}:
				</Box>
				<InboundReferencesContainer>
					<InboundReferencesInput value={maxOfSameType} onChange={onUpdateMaxOfSameType} />
				</InboundReferencesContainer>
			</Box>

			<Text as="p" size="UNSAFE_small">
				{formatMessage(messages.helpText)}
			</Text>
		</>
	);
};

const inputContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: 'space.100',
	maxWidth: '200px',
});

const labelTextStyles = xcss({
	marginRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InboundReferencesContainer = styled2.div({
	/* this is to ensure the inputs are the same width */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		width: '60px',
	},
});
